/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import Paper from '@mui/material/Paper';
import { DataGrid } from '@mui/x-data-grid';
import {
  EMPLOYEES,
  SEARCH_EMPLOYEE,
  CREATE_EMPLOYEE,
  UPDATE_EMPLOYEE,
} from '../../graphql/Employee';
import Box from '@mui/material/Box';
import { FormControl, InputAdornment, OutlinedInput } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { Link } from 'react-router-dom';
import MyBreadcrumbs from '../MyBreadcrumbs';
import GlobalContext from '../../utils/GlobalContext';
import dayjs from 'dayjs';

const EmployeeForm = React.lazy(() => import('./EmployeeForm'));

const EmployeePage = (props) => {
  const { role } = props;
  const { setSnack, setGlobalLoading } = React.useContext(GlobalContext);
  const [employees, setEmployees] = React.useState([]);
  const [baseEmployees, setBaseEmployees] = React.useState([]);
  const [searched, setSearched] = React.useState('');
  const [isSearch, setIsSearch] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [edit, setEdit] = React.useState(false);
  const [data, setData] = React.useState({});
  const [isSubmit, setIsSubmit] = React.useState(false);

  const columns = [
    {
      field: 'id',
      headerName: 'Nomor',
      width: 90,
      renderCell: (params) => `${params.api.getRowIndex(params.row.id) + 1}.`,
    },
    {
      field: 'name',
      headerName: 'Nama Pegawai',
      flex: 1,
    },
    {
      field: 'dob',
      headerName: 'Tempat & Tanggal Lahir',
      flex: 1,
      renderCell: (params) => {
        return `${params?.row?.place_of_birth ?? null}, ${
          dayjs(params?.row?.date_of_birth).format('DD - MM - YYYY') ?? null
        }`;
      },
    },
    {
      field: 'phone',
      headerName: 'Nomor Telepon',
      flex: 1,
    },
    {
      field: 'role',
      headerName: 'Peran & Status',
      flex: 1,
      renderCell: (params) => {
        return `${params?.row?.role} | ${params?.row?.status}`;
      },
    },
  ];

  if (role !== 'MONITORING') {
    columns.push({
      field: 'eid',
      headerName: 'Aksi',
      width: 90,
      renderCell: (params) => {
        return (
          <>
            <Tooltip title='Detail' placement='left'>
              <IconButton
                aria-label='detail'
                size='small'
                component={Link}
                to={`/admin/pegawai/${params.row.eid}`}
                target='__blank'
              >
                <InfoIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Ubah' placement='right'>
              <IconButton
                aria-label='edit'
                size='small'
                onClick={() => {
                  setEdit(true);
                  setData(params?.row);
                  setOpen(true);
                }}
              >
                <EditIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    });
  }

  const requestSearch = (value) => {
    setSearched(value);
  };

  const clearSearch = () => {
    setSearched('');
    setIsSearch(false);
    setEmployees([...baseEmployees]);
  };

  const doSearch = async () => {
    setIsSearch(true);
    const data = await getEmployees({
      variables: { name: searched },
    });

    setEmployees([...data.data?.searchEmployees]);
  };

  /* FETCH DATA EMPLOYEES */
  const { loading: loadEmployees, data: dataEmployees } = useQuery(EMPLOYEES, {
    fetchPolicy: 'network-only',
    onError: (e) => console.log('error getting selor:', e),
  });

  /* FETCH DATA SEARCH EMPLOYEES */
  const [getEmployees] = useLazyQuery(SEARCH_EMPLOYEE, {
    fetchPolicy: 'network-only',
  });

  /* MUTATE DATA */
  const [insertEmployee] = useMutation(CREATE_EMPLOYEE, {
    onCompleted: (d) => {
      const data = employees;
      setEmployees([...data, d.createEmployee]);
      setGlobalLoading(true);
      setSnack({
        variant: 'success',
        message: 'Data Pegawai berhasil disimpan',
        opened: true,
      });
      setGlobalLoading(false);
      setOpen(false);
    },
    onError: (e) => {
      console.log('error verify user: ', e);
      setGlobalLoading(false);
      setSnack({
        variant: 'error',
        message: 'Data Pegawai gagal disimpan',
        opened: true,
      });
    },
  });

  const [updateEmployee] = useMutation(UPDATE_EMPLOYEE, {
    onCompleted: (d) => {
      const data = employees;
      const updatedData = data.map((x) => {
        if (x.id === d.updateEmployee.id) {
          return {
            ...d.updateEmployee,
          };
        }

        return x;
      });

      setEmployees([...updatedData]);
      setGlobalLoading(true);
      setSnack({
        variant: 'success',
        message: 'Data pegawai berhasil diubah',
        opened: true,
      });
      setGlobalLoading(false);
      setOpen(false);
      setEdit(false);
      setData({});
      setIsSubmit(false);
    },
    onError: (e) => {
      console.log('error insert employee: ', e);
      let message = 'Data pegawai gagal diubah';

      setGlobalLoading(false);
      setSnack({
        variant: 'error',
        message,
        opened: true,
      });
    },
  });

  /* BUTTON DIALOG */
  const handleClickOpen = () => {
    setOpen(true);
  };

  /* INSERT DATA */
  const handleSubmit = async (values) => {
    if (edit) {
      setGlobalLoading(true);
      setIsSubmit(true);
      updateEmployee({
        variables: {
          eid: data.eid,
          data: values,
        },
      });
    } else {
      insertEmployee({
        variables: { data: values },
      });
    }
  };

  React.useEffect(() => {
    if (loadEmployees) {
      setGlobalLoading(true);
    }
  }, []);

  React.useEffect(() => {
    if (dataEmployees) {
      setBaseEmployees(dataEmployees.employees);
      setEmployees(dataEmployees.employees);
      setGlobalLoading(false);
    }
  }, [dataEmployees]);

  return (
    <React.Fragment>
      <Box
        sx={{
          display: 'flex',
          bgcolor: 'background.paper',
          borderRadius: 1,
          alignItems: 'center',
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <MyBreadcrumbs data={['Master', 'Pegawai']} />
        </Box>
        <Box
          sx={{
            p: 0.5,
            m: 0.5,
          }}
        >
          <FormControl size='small' margin='dense'>
            <OutlinedInput
              name='search'
              margin='dense'
              id='search'
              placeholder='Cari'
              onChange={(e) => requestSearch(e.target.value)}
              value={searched}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  doSearch();
                }
              }}
              endAdornment={
                <InputAdornment position='end'>
                  {isSearch && (
                    <IconButton onClick={() => clearSearch()}>
                      <ClearIcon />
                    </IconButton>
                  )}
                  {!isSearch && (
                    <IconButton onClick={() => doSearch()}>
                      <SearchIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              }
            />
          </FormControl>
        </Box>
        {role !== 'MONITORING' && (
          <Box
            sx={{
              p: 0.5,
              m: 0.5,
            }}
          >
            <Tooltip title='Tambah' placement='top'>
              <IconButton
                aria-label='add'
                size='small'
                onClick={() => handleClickOpen()}
                style={{ backgroundColor: '#1976d2', color: 'white' }}
              >
                <AddIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <div style={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={employees}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
          />
        </div>
      </Paper>

      <React.Suspense fallback={<div></div>}>
        <EmployeeForm
          open={open}
          setOpen={setOpen}
          handleSubmit={handleSubmit}
          setEdit={setEdit}
          edit={edit}
          data={data}
          isSubmit={isSubmit}
        />
      </React.Suspense>
    </React.Fragment>
  );
};

export default EmployeePage;
